import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';

import {FilterHeader} from 'src/components/filter-header/filter-header';
import {Props as SortableHeaderProps} from 'src/components/filter-header/sortable-header';
import {getQueriedColumnData} from 'src/components/filter-header/utils/getQueriedColumnData';
import {prepareColumnDataWithBlank} from 'src/components/filter-header/utils/prepareColumnDataWithBlank';
import {removeFilteredItems} from 'src/components/filter-header/utils/removeFilteredItems';
import {sortSelectedFirst} from 'src/components/filter-header/utils/sortSelectedFirst';
import {usePagination} from 'src/hooks/usePagination';
import getHighlightedText from 'src/utils/getHighlightedText';

import {FlatButton} from '@pb/kit-component-button';
import CheckBox from '@pb/kit-component-checkbox';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Input} from '@pb/kit-component-input-v2';
import Loading from '@pb/kit-component-loading';
import {Scrollable} from '@pb/kit-component-scrollable';
import {Body, Cell, Row, Table} from '@pb/kit-component-table';

import {content, item, scrollable} from './filter-header.module.styl';

type OwnProps = {
    columnData: Array<string>,
    filters: Array<string>,
    setFilter: (value: Array<string>) => void,
};

type Props = OwnProps & Omit<SortableHeaderProps, 'contentAfter'>;

const AMOUNT_LEFT_TO_UPLOAD_NEXT = 8;
const PAGE_SIZE = 250;

export const ValueFilterHeader = (props: Props) => {
    const {
        columnData,
        setFilter,
        filters,
        caption,
        onSort,
        isSorted,
    } = props;

    const [query, setQuery] = useState<string>('');
    const [checkboxes, setCheckboxes] = useState<Array<string>>(filters);

    const sortedColumnData = useMemo(() => {
        return sortSelectedFirst(columnData, checkboxes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnData]);

    const queriedColumnData = useMemo(() => {
        return getQueriedColumnData(sortedColumnData, query);
    }, [sortedColumnData, query]);

    const {hasBlank, displayedColumnData} = useMemo(() => {
        return prepareColumnDataWithBlank(queriedColumnData, query);
    }, [queriedColumnData, query]);

    const {
        slicedRowsByPage,
        hasMore,
        intersectRef,
        isVisible,
        goToPage,
    } = usePagination<string>(displayedColumnData, PAGE_SIZE);

    useEffect(() => {
        if (filters !== checkboxes) {
            setCheckboxes(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onQueryChange = ({target: {value}}: ChangeEvent<HTMLInputElement>) => {
        setQuery(value);
    };

    const onInputClear = () => {
        setQuery('');
    };

    const selectAllCheckBoxes = () => {
        setCheckboxes([...checkboxes, ...displayedColumnData]);
    };

    const deselectAllCheckBoxes = () => {
        setCheckboxes([...removeFilteredItems(checkboxes, displayedColumnData)]);
    };

    const onCheckBoxChange = (data: {event: React.SyntheticEvent<HTMLInputElement>, name: string}) => {
        const {event, name} = data;

        if (event.currentTarget.checked) {
            setCheckboxes([...checkboxes, name]);
        } else {
            setCheckboxes(checkboxes.filter(checkbox => checkbox !== name));
        }
    };

    const onFilterApply = (onClose?: () => void) => {
        setFilter(checkboxes);
        onInputClear();
        if (onClose) {
            onClose();
        }
    };

    const resetFilters = (onClose?: () => void) => {
        setFilter([]);
        deselectAllCheckBoxes();
        if (onClose) {
            onClose();
        }
    };

    const onCancel = (onClose?: () => void) => {
        if (checkboxes !== filters) {
            setCheckboxes(filters);
        }
        onInputClear();
        if (onClose) {
            onClose();
        }

        goToPage(1);
    };

    const getSelectionButton = () => {
        const isSelectedAll = displayedColumnData.every(data => checkboxes.includes(data));
        const onClick = isSelectedAll ? deselectAllCheckBoxes : selectAllCheckBoxes;

        return (queriedColumnData.length || hasBlank) && (
            <FlatButton onClick={onClick}>
                {isSelectedAll ? 'Deselect All' : 'Select All'}
            </FlatButton>
        );
    };

    return (
        <FilterHeader
            {...{
                isFilterApplied: !!filters.length,
                onFilterApply,
                onCancel,
                resetFilters,
                caption,
                onSort,
                isSorted,
            }}>
            <Group
                wrap="wrap-reverse"
                direction="v"
                justify="space-between"
                alignItems="start">
                <Item style={{width: '100%'}}>
                    <Input
                        name="value-filter-search"
                        onChange={onQueryChange}
                        value={query}
                        placeholder="Filter or select below"
                        autoFocus={true}
                        autoComplete="off"
                        endAdornment={query
                            ? <BasicIconButton iconProps={{type: Icons.CLOSE}} onClick={onInputClear}/>
                            : <Icon type={Icons.SEARCH}/>}
                    />
                </Item>
                {getSelectionButton()}
                <Item className={content} style={{width: 355}}>
                    {queriedColumnData.length > 0 || hasBlank
                        ? (
                                <Scrollable mix={scrollable}>
                                    <Table>
                                        <Body>
                                            {slicedRowsByPage.map((name, i, arr) => (
                                                <Row
                                                    key={i}
                                                    showZebra={false}
                                                    ref={node => (i === arr.length - AMOUNT_LEFT_TO_UPLOAD_NEXT
                                                        ? intersectRef(node as Element) : undefined)}>
                                                    <Cell className={`${item} ellipsis`} title={name}>
                                                        <CheckBox
                                                            label={getHighlightedText(name, query)}
                                                            name={name}
                                                            checked={checkboxes.includes(name)}
                                                            onCheckBoxChange={onCheckBoxChange}
                                                            className="value-filter__checkbox"
                                                        />
                                                    </Cell>
                                                </Row>
                                            ))}
                                        </Body>
                                    </Table>
                                    {(isVisible && hasMore) ? <Loading fast={true}/> : null}
                                </Scrollable>
                            )
                        : <span className="no-matches">No matches found</span>}
                </Item>
            </Group>
        </FilterHeader>
    );
};
